import React, { useState, useEffect, useRef, lazy } from "react";
import Modal from "../../../../components/Modal/Modal";
import Button from "../../../../components/Button/Button";
import Typography from "../../../../components/Typography/Typography";
import DocumentStoreStyles from "./DocumentStore.module.scss";
import UnderMaintanceScreen from "../../../../assets/under-maintance.svg";
import { UploadBtn } from "../../../../components/Buttons/Buttons";
import { fetchFileFromDocumentStore } from "../../../../services/fetchFile.service";
import DocIcon from "../../../../assets/doc.svg";
import xlsxIcon from "../../../../assets/xlsxIcon.svg";
import pdfIcon from "../../../../assets/pdfIcon.svg";
import Doc from "../../../../assets/docIcon.svg";
import { DOCUMENT_STORE } from "../../constants";
import Tooltip from "../../../../components/Tooltip/Tooltip";
import ArrowRightIcon from "../../../../assets/arrow_right.svg";
import Shimmer from "./Shimmer";
import DisabledDoc from "../../../../assets/disabled_folder.svg";

import { useAppDispatch, useAppSelector } from "../../../../hooks/store";
import {
  uploadFileForClauseCheckThunk,
  uploadFileForContractDraftingThunk,
  uploadFileForContractReviewThunk,
  uploadFileForRfxGeneratorThunk,
  uploadFilesForRfxEvaluationThunk,
  uploadMutliDocFromBotThunk,
  setModalOpen
} from "../../store/dashboard.thunk";

import UploadIconOutlined from "../../../../assets/upload_grey.svg";
import { DASHBOARD_ENTITIES } from "../../../../common/constants";
import { isPDF } from "../../utils";

interface DocumentStoreModalProps {
  open: boolean;
  onClose: () => void;
  onCancel: () => void;
  dashboardEntity: string;
}

const DocumentStoreModal: React.FC<DocumentStoreModalProps> = ({
  open,
  onClose,
  onCancel,
  dashboardEntity,
}) => {
  const [selectedItems, setSelectedItems] = useState<any[]>([]);
  const [allFiles, setAllFiles] = useState<any>([]);
  const [loading, setLoading] = useState(true);
  const [path, setPath] = useState<string[]>(["Root"]);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const [selectedIndexes, setSelectedIndexes] = useState<number[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  const itemsCount = allFiles?.file_details?.length ?? 0;

  const dispatch = useAppDispatch();
  const getEntityState = (dashboardEntity: string, store: any) => {
    switch (dashboardEntity) {
      case DASHBOARD_ENTITIES.RFX_GENERATOR:
        return store?.rootReducer?.vscDashboardReducer?.rfxGenerator;
      case DASHBOARD_ENTITIES.CONTRACT_DRAFTING:
        return store?.rootReducer?.vscDashboardReducer?.contractDrafting;
      case DASHBOARD_ENTITIES.CONTRACT_REVIEW:
        return store?.rootReducer?.vscDashboardReducer?.contractsReview;
      case DASHBOARD_ENTITIES.RFX_EVALUATION:
        return store?.rootReducer?.vscDashboardReducer?.rfxEvaluation;
      case DASHBOARD_ENTITIES.CLAUSE_CHECK:
        return store?.rootReducer?.vscDashboardReducer?.clauseCheck;
      case DASHBOARD_ENTITIES.TALK_TO_CONTRACT:
        return store?.rootReducer?.vscDashboardReducer?.vmoBot;
      default:
        return {};
    }
  };
  const entityToFeatureMap: Record<string, string> = {
    [DASHBOARD_ENTITIES.RFX_GENERATOR]: "RFX_GENERATION",
    [DASHBOARD_ENTITIES.CONTRACT_DRAFTING]: "CONTRACT_DRAFTING",
    [DASHBOARD_ENTITIES.CONTRACT_REVIEW]: "CONTRACT_REDLINING",
    [DASHBOARD_ENTITIES.RFX_EVALUATION]: "RFX_EVALUATION",
    [DASHBOARD_ENTITIES.CLAUSE_CHECK]: "KEY_CONTRACT_CLAUSES",
    [DASHBOARD_ENTITIES.TALK_TO_CONTRACT]: "TALK_TO_CONTRACT",
  };
  const feature = entityToFeatureMap[dashboardEntity];
  const entityState = useAppSelector((store) =>
    getEntityState(dashboardEntity, store)
  );
  const [unifiedUploadState, setUnifiedUploadState] = useState<{
    isUploading: boolean;
    uploaded: boolean;
    error: any;
    selectedTemplate: any;
    rfxGeneratorSelectedTemplate: any;
  }>({
    isUploading: false,
    uploaded: false,
    error: null,
    selectedTemplate: null,
    rfxGeneratorSelectedTemplate: null,
  });

  useEffect(() => {
    let isUploading = false;
    let uploaded = false;
    let error = null;
    let currentSelectedTemplate = null;
    let currentRfxGeneratorSelectedTemplate = null;

    if (entityState) {
      switch (dashboardEntity) {
        case DASHBOARD_ENTITIES.RFX_GENERATOR:
          isUploading = entityState.fileUpload?.isUploading;
          uploaded = entityState.fileUpload?.uploaded;
          error = entityState.fileUpload?.error;
          currentRfxGeneratorSelectedTemplate =
            entityState.rfxGeneratorSelectedTemplate;
          break;
        case DASHBOARD_ENTITIES.CONTRACT_DRAFTING:
          isUploading = entityState.fileUpload?.isUploading;
          uploaded = entityState.fileUpload?.uploaded;
          error = entityState.fileUpload?.error;
          currentSelectedTemplate = entityState.selectedTemplate;
          break;
        case DASHBOARD_ENTITIES.CONTRACT_REVIEW:
          isUploading = entityState.pending_documents?.fileUpload?.isUploading;
          uploaded = entityState.pending_documents?.fileUpload?.uploaded;
          error = entityState.pending_documents?.fileUpload?.error;
          break;
        case DASHBOARD_ENTITIES.RFX_EVALUATION:
          isUploading = entityState.docsUploading;
          uploaded = entityState.docsUploaded;
          error = entityState.error;
          break;
        case DASHBOARD_ENTITIES.CLAUSE_CHECK:
          isUploading = entityState.loading;
          uploaded = entityState.uploadProcessCompleted;
          error = entityState.error;
          break;
        case DASHBOARD_ENTITIES.TALK_TO_CONTRACT:
          isUploading = entityState.isUploading;
          uploaded = entityState.uploadProcessCompleted;
          error = entityState.error;
          break;
        default:
          break;
      }
    }
    setUnifiedUploadState({
      isUploading,
      uploaded,
      error,
      selectedTemplate: currentSelectedTemplate,
      rfxGeneratorSelectedTemplate: currentRfxGeneratorSelectedTemplate,
    });
  }, [entityState, dashboardEntity]);

  const selectedTemplate = entityState?.selectedTemplate;
  const rfxGeneratorSelectedTemplate =
    entityState?.rfxGeneratorSelectedTemplate;
  const styles = {
    borderRadius: "24px",
    padding: "4px 16px",
    marginTop: "10px",
  };

  const hasFetchedData = useRef(false);

  const getItemStyle = (item: any, isHovered: boolean) => ({
    border: selectedItems.some(
      (selectedItem) => selectedItem.name === item.name
    )
      ? "1px solid var(--Border-Primary, #3474E0)"
      : "1px solid var(--Border-Neutral-Subtle, #E4E7EC)",
    backgroundColor: isHovered
      ? selectedItems.some((selectedItem) => selectedItem.name === item.name)
        ? "var(--Surface-Surface-2, #F2F4F7)"
        : "var(--Surface-Surface-1, #FCFCFD)"
      : selectedItems.some((selectedItem) => selectedItem.name === item.name)
      ? "var(--Background-Primary-Subtle-3, #F1F7FF)"
      : "var(--Surface-Surface-1, #FCFCFD)",
  });

  const getImgStyles = (imageUrl: string) => ({
    paddingTop: imageUrl === "docx" ? "4px" : undefined,
  });

  const fetchData = async (item?: any, folderPath?: Array<string>) => {
    setLoading(true);

    try {
      if (item?.type !== "file") {
        setPath((prevPath) => {
          // Create a new path array, add the new item, and filter out undefined values
          const newPath = [...prevPath, item?.name].filter(Boolean);

          // Use a Set to remove duplicates and convert it back to an array
          const uniquePath = Array.from(new Set(newPath));

          return uniquePath;
        });
        const updatedPath = path
          .map((folder) => (folder === "Root" ? "root" : folder))
          .join("/");
        const backTraversingPath = folderPath
          ?.map((folder) => (folder === "Root" ? "root" : folder))
          .join("/");
        const finalPath =
          item?.type === "folder" && !updatedPath.endsWith(item.name)
            ? `${updatedPath}/${item.name}`
            : updatedPath;

        const res = await fetchFileFromDocumentStore(
          feature,
          item?.type === "folder"
            ? backTraversingPath
              ? backTraversingPath
              : finalPath
            : "root"
        );

        setLoading(false);

        setAllFiles(res);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && !hasFetchedData.current) {
      fetchData();
      hasFetchedData.current = true;
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      hasFetchedData.current = false;
    }
  }, [open]);

  const handleFolderClick = (item: any) => {
    if (item.type === "folder") {
      setPath((prevPath) => [...prevPath, item.name]);
      setSelectedItems([]);
      fetchData(item);
    }
  };
 const handleItemClick = (item: any) => {
  setSelectedItems((prevSelectedItems) => {
    // Multi-select logic for specific entities
    if (
      dashboardEntity === DASHBOARD_ENTITIES.RFX_EVALUATION ||
      dashboardEntity === DASHBOARD_ENTITIES.CLAUSE_CHECK ||
      dashboardEntity === DASHBOARD_ENTITIES.TALK_TO_CONTRACT
    ) {
      if (item.type === "folder") {
        // If folder is already selected, deselect it
        if (
          prevSelectedItems.length === 1 &&
          prevSelectedItems[0].name === item.name &&
          prevSelectedItems[0].type === "folder"
        ) {
          return [];
        }
        // Only allow one folder to be selected at a time
        return [item];
      } else {
        // If a folder is currently selected, replace with the file
        if (
          prevSelectedItems.length === 1 &&
          prevSelectedItems[0].type === "folder"
        ) {
          return [item];
        }
        // Toggle file selection (multi-select)
        if (
          prevSelectedItems.some(
            (selectedItem) => selectedItem.name === item.name
          )
        ) {
          return prevSelectedItems.filter(
            (selectedItem) => selectedItem.name !== item.name
          );
        } else {
          return [...prevSelectedItems, item];
        }
      }
    } else {
      // Single select for other entities
      if (
        prevSelectedItems.some(
          (selectedItem) => selectedItem.name === item.name
        )
      ) {
        return [];
      } else {
        return [item];
      }
    }
  });
};
  const [isHovered, setIsHovered] = useState(false);

  const handleMouseEnter = () => setIsHovered(true);
  const handleMouseLeave = () => setIsHovered(false);

  const fileExtensionToImageMap: Record<string, string> = {
    ".pdf": pdfIcon,
    ".xlsx": xlsxIcon,
    ".docx": Doc,
  };

  const getImageForFile = (fileName: string) => {
    const fileExtension = fileName
      .slice(fileName.lastIndexOf("."))
      .toLowerCase();

    return fileExtensionToImageMap[fileExtension] || DocIcon;
  };

  const trimFileName = (fileName: string, maxLength = 23) => {
    if (fileName.length > maxLength) {
      return `${fileName.slice(0, maxLength)}...`;
    }
    return fileName;
  };

  const handleModalBodyClick = (e: React.MouseEvent<HTMLDivElement>) => {
    
    if (
      e.target instanceof HTMLElement &&
      !['INPUT', 'BUTTON', 'A', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)
    ) {
      containerRef.current?.focus();
    }
    e.stopPropagation(); 
  };
  const handleBreadcrumbClick = (index: number) => {
    if (index < path.length - 1) {
      const newPath = path.slice(0, index + 1).filter(Boolean);

      setPath(newPath);
      setSelectedItems([]);
      setAllFiles([]);

      fetchData({ type: "folder" }, path.slice(0, index + 1).filter(Boolean));
    }
  };

  const handleUpload = async () => {
    if (dashboardEntity === DASHBOARD_ENTITIES.CONTRACT_DRAFTING) {
      dispatch(
        uploadFileForContractDraftingThunk({
          file: selectedItems[0].filepath_gcs_uri,
          fileDetails: {
            file_name: selectedItems[0].name,
            id: selectedItems[0].id,
          },
          isLocal: false,
          selectedTemplate: selectedTemplate,
          isUploadFromLocalOrDrive: false,
        })
      );
    } else if (dashboardEntity === DASHBOARD_ENTITIES.RFX_GENERATOR) {
      dispatch(
        uploadFileForRfxGeneratorThunk({
          file: selectedItems[0].filepath_gcs_uri,
          fileDetails: {
            file_name: selectedItems[0].name,
            id: selectedItems[0].id,
          },
          isLocal: false,
          rfxGeneratorSelectedTemplate: rfxGeneratorSelectedTemplate,
          isUploadFromLocalOrDrive: false,
        })
      );
    } else if (dashboardEntity === DASHBOARD_ENTITIES.CONTRACT_REVIEW) {
      dispatch(
        uploadFileForContractReviewThunk({
          file: selectedItems[0].filepath_gcs_uri,
          fileDetails: {
            file_name: selectedItems[0].name,
            id: selectedItems[0].id,
          },
          isLocal: false,
          isUploadFromLocalOrDrive: false,
        })
      );
    } else if (dashboardEntity === DASHBOARD_ENTITIES.RFX_EVALUATION) {
      dispatch(
        uploadFilesForRfxEvaluationThunk({
          docsFromDrive: selectedItems,
          filesFromLocal: [],
          isLocal: false,
          isUploadFromLocalOrDrive: false,
        })
      );
    } else if (dashboardEntity === DASHBOARD_ENTITIES.CLAUSE_CHECK) {
      dispatch(
        uploadFileForClauseCheckThunk({
          docs: selectedItems,
          isLocal: false,
          isUploadFromLocalOrDrive: false,
        })
      );
    } else if (dashboardEntity === DASHBOARD_ENTITIES.TALK_TO_CONTRACT) {
      dispatch(
        uploadMutliDocFromBotThunk({
          docs: selectedItems,
          isLocal: false,
          isUploadFromLocalOrDrive: false,
        })
      );
    }
     dispatch(setModalOpen(false));
  };
  const hasHandledUpload = useRef(false);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (
      e.shiftKey &&   (    e.key === "ArrowDown" ||     e.key === "ArrowUp" ||     e.key === "ArrowLeft" || e.key === "ArrowRight" )
    ) {
      e.preventDefault();
  
      const itemsCount = allFiles?.file_details?.length ?? 0;
      if (itemsCount === 0) return;
  
      let newIndex = focusedIndex;
      if (e.key === "ArrowDown" || e.key === "ArrowRight") {
        newIndex = Math.min(itemsCount - 1, focusedIndex + 1);
      } else if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
        newIndex = Math.max(0, focusedIndex - 1);
      }
  
      setFocusedIndex(newIndex);
      if (e.shiftKey) {
        let anchor =
          selectedIndexes.length > 0 ? selectedIndexes[0] : (typeof focusedIndex === 'number' ? focusedIndex : 0);
        const [start, end] = [anchor, newIndex].sort((a, b) => a - b);
      
        // Only allow a multi-range if all are PDFs
        const filesInRange = allFiles.file_details.slice(start, end + 1);
        if (filesInRange.every((f:any) => isPDF(f.name))) {
          const range = [];
          for (let i = start; i <= end; ++i) range.push(i);
          setSelectedIndexes(range);
        } else {
          // If not all PDFs, just select one
          setSelectedIndexes([newIndex]);
        }
      } else {
        setSelectedIndexes([newIndex]);
      }
      return; // Prevent handling Enter at the same time as arrows!
    }
  
    if (e.key === "Enter") {
      // Only upload if a selection exists (or only for files if desired)
      if (
        selectedIndexes.length > 0 &&
        selectedIndexes.every(idx => allFiles.file_details[idx]?.type === "file")
      ) {
        handleUpload();
      }else{
        if (selectedItems[0].type === "folder") {
          setPath((prevPath:any) => [...prevPath, selectedItems[0].name]);
          setSelectedItems([]);
          fetchData(selectedItems[0]);
        }
      }
    }
  };

  useEffect(() => {
    if (open) {
      const id = setTimeout(() => {
        containerRef.current?.focus();
      }, 50);
      return () => clearTimeout(id);
    }
  }, [open, allFiles,selectedItems]);

  useEffect(() => {
    if (!allFiles?.file_details) return;
    setSelectedItems(selectedIndexes.map(idx => allFiles.file_details[idx]));
  }, [selectedIndexes, allFiles]);

  useEffect(() => {
    if (open && unifiedUploadState.uploaded && !hasHandledUpload.current) {
      hasHandledUpload.current = true;
      onCancel();
    }
    if (!open) {
      hasHandledUpload.current = false;
    }
  }, [open, unifiedUploadState.uploaded, onCancel]);
  return (
    <div onClick={handleModalBodyClick}>
      <Modal
        open={open}
        onCancel={onCancel}
        title="Upload Documents"
        width="1000px"
        footer={[
          <div className={DocumentStoreStyles.modalFooter}>
            {loading ||
            unifiedUploadState?.error?.response?.data?.status_message ==
              "Store under maintenance" ? (
              <div style={{ height: "36px" }}></div>
            ) : (
              <div className={DocumentStoreStyles.btnContainer}>
                <Button variant="outlined" onClick={onCancel} style={styles}>
                  <Typography fontSize="12px">Cancel</Typography>
                </Button>
                <UploadBtn
                  onClick={handleUpload}
                  customStyle={styles}
                  disabled={
                    selectedItems.length === 0 ||
                    selectedItems.some(
                      (item: { type: string }) => item?.type === "folder"
                    )
                  }
                  loading={unifiedUploadState?.isUploading}
                  startIcon={
                    unifiedUploadState?.isUploading && (
                      <img src={UploadIconOutlined} alt="" />
                    )
                  }
                />
              </div>
            )}
          </div>,
        ]}
      >
        {loading ? (
          <div className={DocumentStoreStyles.uploadContainerFolder}>
            <Typography
              style={{
                padding: "15px 0px",
                borderTop: "1px solid var(--Border-Neutral-Default, #abb1ba)",
              }}
              color="#282B31"
              fontSize={12}
              fontWeight={500}
            >
              Folders
            </Typography>
            <div className={DocumentStoreStyles.shimmerContainer}>
              {[...Array(4)].map((_, index) => (
                <Shimmer key={index} />
              ))}
            </div>
          </div>
        ) : (
          <>
            {unifiedUploadState.error?.response?.data?.status_message ===
            "Store under maintenance" ? (
              <div className={DocumentStoreStyles.underMaintenance}>
                <img
                  src={UnderMaintanceScreen}
                  height="inherit"
                  alt="Under Maintenance"
                  style={{ maxWidth: "fit-content" }}
                />
              </div>
            ) : (
              <div
                className={DocumentStoreStyles.uploadContainer}
                style={{ maxHeight: "400px", overflowY: "auto" }}
              >
                <div className={DocumentStoreStyles.breadcrumb}>
                  {path.map((folder, index) => (
                    <div
                      className={`${DocumentStoreStyles.breadcrumbItem} ${
                        index === path.length - 1
                          ? DocumentStoreStyles.activeBreadcrumb
                          : ""
                      }`}
                      key={index}
                      onClick={() => handleBreadcrumbClick(index)}
                    >
                      {index === path.length - 1 ? (
                        <img src={DisabledDoc} />
                      ) : (
                        <img src={DocIcon} />
                      )}
                      <div> {folder}</div>{" "}
                      {index === path.length - 1 ? null : (
                        <img src={ArrowRightIcon} />
                      )}
                    </div>
                  ))}
                </div>
                {!(allFiles?.file_details?.length === 0) ? (
                  <div className={DocumentStoreStyles.uploadContainerFolder}>
                    <Typography
                      style={{ padding: "15px 0px" }}
                      color="#282B31"
                      fontSize={12}
                      fontWeight={500}
                    >
                      Folders
                    </Typography>
                    <div className={DocumentStoreStyles.uploadContainerContent}
                       ref={containerRef}
                       tabIndex={0}
                       onKeyDown={handleKeyDown}
                    >
                      {allFiles?.file_details?.map(
                        (item: any, index: number) => {
                          const imageUrl = getImageForFile(item.name);
                          const displayName = trimFileName(item.name);

                          return (
                            <>
                              <div
                                className={`
                                  ${DocumentStoreStyles.item}
                                  ${selectedIndexes.includes(index) ? DocumentStoreStyles.selectedItem : ""}
                                  ${focusedIndex === index ? DocumentStoreStyles.focusedItem : ""}
                                `}
                                key={index}
                                onClick={() => handleItemClick(item)}
                                onDoubleClick={() => handleFolderClick(item)}    
                                style={getItemStyle(item, isHovered)}
                                onMouseEnter={handleMouseEnter}
                                onMouseLeave={handleMouseLeave}
                              >
                                <Typography
                                  style={{ padding: "13px 15px" }}
                                  color="#282B31"
                                  fontSize={12}
                                  fontWeight={500}
                                >
                                  {imageUrl && (
                                    <div
                                      className={
                                        DocumentStoreStyles.imageContainer
                                      }
                                    >
                                      {" "}
                                      <img
                                        src={imageUrl}
                                        alt={item.name}
                                        style={getImgStyles(imageUrl)}
                                      />{" "}
                                      <Tooltip
                                        title={
                                          item.name.length > 20 ? item.name : ""
                                        }
                                        placement="right"
                                        overlayInnerStyle={{
                                          overflow: "auto",
                                          maxHeight: "300px",
                                        }}
                                      >
                                        <p
                                          className={
                                            DocumentStoreStyles.itemName
                                          }
                                        >
                                          {displayName}
                                        </p>
                                      </Tooltip>
                                    </div>
                                  )}
                                </Typography>
                              </div>{" "}
                            </>
                          );
                        }
                      )}
                    </div>
                  </div>
                ) : (
                  <div className={DocumentStoreStyles.noFile}>
                    {DOCUMENT_STORE.NO_FILE}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </Modal>
    </div>
  );
};

export default DocumentStoreModal;
